import { useState, useEffect } from "react";
import useSWR, { mutate } from "swr";
import { toast } from "sonner";
import { Plus, Buildings, Trash, PencilSimple, X, ArrowRight } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/app";
import { useAuth } from "@/lib/auth";

const fetcher = (url) => api.get(url).then((r) => r.data);


function CompanyRow({ company, onDelete, onEdit, isDeveloper }) {
  const { data: stores = [] } = useSWR(`/companies/${company.id}/stores`, fetcher);
  return (
    <div className="card-flat overflow-hidden" data-testid={`company-row-${company.subdomain}`}>
      <div className="flex items-center gap-3 p-4">
        <div className="w-10 h-10 rounded-md bg-[var(--brand)]/10 text-[var(--brand)] flex items-center justify-center shrink-0">
          <Buildings size={20} weight="duotone" />
        </div>
        <Link
          to={`/klienti/${company.id}`}
          className="flex-1 min-w-0 group"
          data-testid={`company-open-${company.subdomain}`}
        >
          <div className="font-display text-base font-semibold truncate group-hover:text-[var(--brand)] transition-colors" data-testid={`company-name-${company.subdomain}`}>
            {company.name}
          </div>
          <div className="text-xs text-[var(--text-muted)] tabular flex items-center gap-3">
            <span>{company.subdomain}.tido.sk</span>
            <span>·</span>
            <span>{stores.length} {stores.length === 1 ? "stredisko" : (stores.length >= 2 && stores.length <= 4 ? "strediská" : "stredísk")}</span>
          </div>
        </Link>
        {isDeveloper && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => onEdit(company)}
              className="text-[var(--text-muted)] hover:text-[var(--brand)] transition-colors p-1.5"
              title="Upraviť"
              data-testid={`company-edit-${company.subdomain}`}
            >
              <PencilSimple size={16} />
            </button>
            <button
              type="button"
              onClick={() => onDelete(company)}
              className="text-[var(--text-muted)] hover:text-[var(--critical)] transition-colors p-1.5"
              title="Zmazať klienta"
              data-testid={`company-delete-${company.subdomain}`}
            >
              <Trash size={16} />
            </button>
          </div>
        )}
        <Link
          to={`/klienti/${company.id}`}
          className="text-[var(--brand)] hover:text-[var(--brand-hover)] p-1.5"
          data-testid={`company-arrow-${company.subdomain}`}
          title="Otvoriť strediská"
        >
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}


function CompanyFormDialog({ company, onClose }) {
  const isEdit = !!company;
  const [name, setName] = useState(company?.name || "");
  const [subdomain, setSubdomain] = useState(company?.subdomain || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !subdomain.trim()) {
      toast.error("Vyplňte meno aj subdoménu.");
      return;
    }
    setSaving(true);
    try {
      const body = { name: name.trim(), subdomain: subdomain.trim().toLowerCase() };
      if (isEdit) {
        await api.put(`/companies/${company.id}`, body);
        toast.success("Klient upravený");
      } else {
        await api.post("/companies", body);
        toast.success("Klient vytvorený");
      }
      mutate("/companies");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba pri ukladaní");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-testid="company-form-dialog">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border-default)]">
          <h2 className="font-display text-xl font-semibold">
            {isEdit ? "Upraviť klienta" : "Nový klient"}
          </h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]" aria-label="Zavrieť">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="label-eyebrow block mb-1">Názov klienta</label>
            <input
              data-testid="company-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="COOP Poprad"
              className="w-full"
              autoFocus
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Subdoména</label>
            <div className="flex items-center gap-2">
              <input
                data-testid="company-subdomain-input"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                placeholder="cooppp"
                className="flex-1"
              />
              <span className="text-sm text-[var(--text-muted)] tabular">.tido.sk</span>
            </div>
            <p className="text-[11px] text-[var(--text-muted)] mt-1">
              Presne tá časť, ktorá je pred „.tido.sk". Napr. <code>cooppp</code>, <code>karmen</code>.
              Ak klient príde z odkazu z inej subdomény, systém ho pod tohto klienta priradí.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[var(--border-default)]">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="company-cancel-btn">Zrušiť</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="company-submit-btn">
              {saving ? "Ukladám…" : isEdit ? "Uložiť zmeny" : "Vytvoriť klienta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


export default function Klienti() {
  const { data: companies = [], isLoading } = useSWR("/companies", fetcher);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDeveloper = user?.role === "developer";

  // Non-developer roles never see the multi-client list — redirect to their
  // own client's detail page. Prevádzka is handled elsewhere (locked-store).
  useEffect(() => {
    if (!user) return;
    if (isDeveloper) return;
    const cid = user.company_id;
    if (cid) {
      navigate(`/klienti/${cid}`, { replace: true });
    }
  }, [user, isDeveloper, navigate]);

  const handleDelete = async (company) => {
    if (!confirm(
      `Naozaj zmazať klienta „${company.name}"?\n\n` +
      `Strediská pod ním zostanú (odpojené), ale klient sa už nebude dať priradiť ` +
      `automaticky pri príchode zo subdomény ${company.subdomain}.tido.sk.`
    )) return;
    try {
      const { data } = await api.delete(`/companies/${company.id}`);
      toast.success(
        `Klient zmazaný` +
        (data.stores_unassigned ? ` (odpojených ${data.stores_unassigned} stredísk)` : "")
      );
      mutate("/companies");
      mutate("/stores");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nepodarilo sa zmazať");
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 py-8">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow mb-2">Správa</p>
          <h1 className="font-display text-4xl font-bold tracking-tight">Klienti</h1>
          <p className="text-[var(--text-secondary)] mt-2 max-w-xl">
            Registruj klientov (TiDo tenantov) podľa subdomény. Ak niekto príde z linky
            <code className="mx-1">?IDP=X&amp;subdomena=Y</code>, systém stredisko priradí
            klientovi podľa <strong>subdomény</strong>. Neregistrované subdomény sú odmietnuté.
          </p>
        </div>
        {isDeveloper && (
          <button
            data-testid="new-company-button"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} weight="bold" /> Pridať klienta
          </button>
        )}
      </div>

      {isLoading && <div className="text-[var(--text-secondary)]">Načítavam…</div>}

      {!isLoading && companies.length === 0 && (
        <div className="card-flat p-12 text-center" data-testid="empty-companies">
          <Buildings size={40} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <h3 className="font-display text-xl font-semibold">Zatiaľ žiadni klienti</h3>
          <p className="text-[var(--text-secondary)] mt-2 max-w-md mx-auto">
            {isDeveloper
              ? <>Pridajte prvého klienta — napríklad <strong>COOP Poprad</strong> so subdoménou <code>cooppp</code>.</>
              : <>Nemáš priradeného žiadneho klienta. Kontaktuj administrátora.</>}
          </p>
          {isDeveloper && (
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="btn-primary mt-5 inline-flex items-center gap-2"
              data-testid="empty-new-company-btn"
            >
              <Plus size={16} weight="bold" /> Pridať prvého klienta
            </button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {companies.map((c) => (
          <CompanyRow
            key={c.id}
            company={c}
            isDeveloper={isDeveloper}
            onDelete={handleDelete}
            onEdit={(comp) => { setEditing(comp); setShowForm(true); }}
          />
        ))}
      </div>

      {showForm && (
        <CompanyFormDialog
          company={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}
